import { zodResolver } from '@hookform/resolvers/zod';
import { Copy, Plus, WebhooksLogo } from '@phosphor-icons/react';
import type {
  CreatedWebhookEndpointDto,
  WebhookEndpointDto,
  WebhookEvent,
} from '@reelplicate/api-client/model';
import {
  getListWebhookEndpointsQueryKey,
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useListWebhookEndpoints,
} from '@reelplicate/api-client/webhook-endpoints';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Field,
  IconButton,
  Modal,
  ModalClose,
  ModalContent,
  Skeleton,
  Stack,
  TextInput,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { relativeTime } from '@/features/notifications/to-entry';

import { ListRow, SettingsCard, SettingsRow, SettingsSection } from './settings-layout';

/** The events, in the order people think about them, with what each means. */
const WEBHOOK_EVENTS: { id: WebhookEvent; label: string }[] = [
  { id: 'post.scheduled', label: 'Post scheduled' },
  { id: 'post.published', label: 'Post published' },
  { id: 'draft.ready', label: 'Draft ready for review' },
  { id: 'draft.decided', label: 'Draft approved or rejected' },
  { id: 'run.finished', label: 'Flow run finished' },
  { id: 'member.joined', label: 'Member joined' },
];

const labelOf = (event: WebhookEvent) =>
  WEBHOOK_EVENTS.find((e) => e.id === event)?.label ?? event;

/** "Last delivery succeeded 2h ago", "Failing: Answered 500", "No deliveries yet". */
function deliveryLine(endpoint: WebhookEndpointDto) {
  const last = endpoint.lastDelivery;
  if (!last) return 'No deliveries yet';
  const when = relativeTime(new Date(last.createdAt), new Date()).toLowerCase();
  if (last.status === 'SUCCEEDED') return `Last delivery succeeded ${when}`;
  if (last.status === 'FAILED')
    return `Last delivery failed ${when}: ${last.lastError ?? ''}`;
  return `Retrying: ${last.lastError ?? 'no answer yet'}`;
}

/**
 * Settings → API & MCP → Webhooks: URLs that get a signed POST when
 * something happens in the workspace. Owners and editors manage them.
 */
export function WebhooksSection({
  workspaceId,
  canManage,
}: {
  workspaceId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: endpoints } = useListWebhookEndpoints(workspaceId, {
    query: { enabled: Boolean(workspaceId) && canManage },
  });
  const remove = useDeleteWebhookEndpoint();
  const [adding, setAdding] = useState(false);
  const [addCount, setAddCount] = useState(0);
  const [removing, setRemoving] = useState<WebhookEndpointDto | null>(null);

  const removeEndpoint = async (endpoint: WebhookEndpointDto) => {
    try {
      await remove.mutateAsync({ id: workspaceId, endpointId: endpoint.id });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListWebhookEndpointsQueryKey(workspaceId),
    });
    toast.success('Endpoint removed');
  };

  return (
    <SettingsSection
      title="Webhooks"
      description="Get a signed POST when something happens in the workspace."
      action={
        canManage && (
          <Button
            variant="outline"
            size="sm"
            leadingIcon={Plus}
            onClick={() => {
              setAddCount((n) => n + 1);
              setAdding(true);
            }}
          >
            Add endpoint
          </Button>
        )
      }
    >
      <SettingsCard>
        {!canManage ? (
          <SettingsRow
            label="Owners and editors manage webhooks"
            description="Ask one of them to add an endpoint."
          />
        ) : !endpoints ? (
          <Skeleton className="my-4 h-10" />
        ) : endpoints.length === 0 ? (
          <SettingsRow
            label="No endpoints yet"
            description="Post scheduled or published, draft ready for review, flow run finished, member joined."
          />
        ) : (
          endpoints.map((endpoint) => (
            <ListRow
              key={endpoint.id}
              tile={<WebhooksLogo aria-hidden className="text-icon-primary size-5" />}
              title={endpoint.description ?? endpoint.url}
              meta={`${endpoint.description ? `${endpoint.url} · ` : ''}${deliveryLine(endpoint)}`}
            >
              <Badge className="hidden sm:inline-flex">
                {endpoint.events.length === 1 && endpoint.events[0]
                  ? labelOf(endpoint.events[0])
                  : `${endpoint.events.length} events`}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Remove ${endpoint.url}`}
                onClick={() => setRemoving(endpoint)}
              >
                Remove
              </Button>
            </ListRow>
          ))
        )}
      </SettingsCard>

      {canManage && (
        <AddEndpointModal
          key={addCount}
          workspaceId={workspaceId}
          open={adding}
          onOpenChange={setAdding}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Remove this endpoint?"
        description={`${removing?.url ?? ''} gets nothing more, including retries still waiting.`}
        confirmLabel="Remove"
        onConfirm={() => removing && void removeEndpoint(removing)}
      />
    </SettingsSection>
  );
}

const EndpointSchema = z.object({
  url: z
    .string()
    .trim()
    .url('Enter a full URL, like https://example.com/hooks')
    .startsWith('https://', 'Use an https:// address'),
  description: z.string().trim().max(200, 'Keep it under 200 characters'),
  events: z.array(z.string()).min(1, 'Pick at least one event'),
});
type EndpointValues = z.infer<typeof EndpointSchema>;

/** Add an endpoint, then show its signing secret once. Mount with a new `key` per opening. */
function AddEndpointModal({
  workspaceId,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const create = useCreateWebhookEndpoint();
  const [created, setCreated] = useState<CreatedWebhookEndpointDto | null>(null);
  const form = useForm<EndpointValues>({
    resolver: zodResolver(EndpointSchema),
    defaultValues: { url: '', description: '', events: [] },
  });

  const onSubmit = form.handleSubmit(async ({ url, description, events }) => {
    try {
      setCreated(
        await create.mutateAsync({
          id: workspaceId,
          data: {
            url,
            events: events as WebhookEvent[],
            description: description || undefined,
          },
        }),
      );
    } catch {
      return; // The failure is already shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListWebhookEndpointsQueryKey(workspaceId),
    });
  });

  if (created) {
    return (
      <Modal open={open} onOpenChange={onOpenChange}>
        <ModalContent
          size="sm"
          title="Endpoint added"
          description="Check the Reelplicate-Signature header on each delivery with this secret."
          footer={
            <ModalClose asChild>
              <Button>Done</Button>
            </ModalClose>
          }
        >
          <Stack gap={4}>
            <Field label="Signing secret">
              <TextInput
                readOnly
                value={created.secret}
                onFocus={(e) => e.currentTarget.select()}
                trailingAction={
                  <IconButton
                    icon={Copy}
                    size="sm"
                    variant="ghost"
                    aria-label="Copy signing secret"
                    onClick={() => {
                      navigator.clipboard?.writeText(created.secret).then(
                        () => toast.success('Signing secret copied'),
                        () =>
                          toast.error(
                            'Couldn’t copy. Select the secret and copy it instead.',
                          ),
                      );
                    }}
                  />
                }
              />
            </Field>
            <Well tone="warning" title="Copy it now">
              This is the only time the secret is shown. If you lose it, remove the
              endpoint and add it again.
            </Well>
          </Stack>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="sm"
        title="Add endpoint"
        description="We POST JSON to it, signed, and retry for about a day if it doesn’t answer 2xx."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button type="submit" form={formId} loading={create.isPending}>
              Add endpoint
            </Button>
          </>
        }
      >
        <Stack
          as="form"
          id={formId}
          gap={4}
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Controller
            name="url"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="URL" error={fieldState.error?.message}>
                <TextInput
                  {...field}
                  placeholder="https://example.com/hooks"
                  autoComplete="off"
                  inputMode="url"
                />
              </Field>
            )}
          />
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Description"
                helper="Optional"
                error={fieldState.error?.message}
              >
                <TextInput
                  {...field}
                  placeholder="Zapier: post to Slack"
                  autoComplete="off"
                />
              </Field>
            )}
          />
          <Controller
            name="events"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Events" error={fieldState.error?.message}>
                <Stack gap={2} role="group" aria-label="Events">
                  {WEBHOOK_EVENTS.map((event) => (
                    <Checkbox
                      key={event.id}
                      label={event.label}
                      checked={field.value.includes(event.id)}
                      onCheckedChange={(checked) =>
                        field.onChange(
                          checked === true
                            ? [...field.value, event.id]
                            : field.value.filter((id) => id !== event.id),
                        )
                      }
                    />
                  ))}
                </Stack>
              </Field>
            )}
          />
        </Stack>
      </ModalContent>
    </Modal>
  );
}
